import _ from "lodash";
import React from "react";
import { Badge } from "@toddle-edu/ds-web";

import {
  getPYPElementSetFromNodes,
  generateNestedSelectedData,
  mergeSelectedNodes,
} from "Utils";
import ACLStore from "lib/aclStore";

// constants
import {
  ELEMENT_TYPE_MYP_ATL,
  ELEMENT_TYPE_DP_ATL,
  ELEMENT_TYPE_UBD_ATL,
  ELEMENT_TYPE_MYP_LEARNING_STANDARD,
  ELEMENT_TYPE_UBD_LEARNING_STANDARD,
  ELEMENT_TYPE_DP_LEARNING_STANDARD,
  ELEMENT_TYPE_MYP_OBJECTIVES,
  ELEMENT_TYPE_DP_SYLLABUS,
  ELEMENT_TYPE_PYP_ATL,
  ELEMENT_TYPE_PYP_LEARNING_STANDARD,
  ELEMENT_TYPE_DP_KNOWLEDGE_FRAMEWORK,
  ELEMENT_TYPE_MYP_RELATED_CONCEPT,
  ELEMENT_TYPE_MYP_SPECIFIED_CONCEPT,
  COURSE_BASED_GRADING,
  ELEMENT_TYPE_UBD_SKILL,
} from "Constants/stringConstants";
import { DATA_GRID_COLUMN_TYPES } from "StandardsTable/modules/constants";
import getCheckboxValue from "StandardsTable/modules/cellUtils/getCheckboxValue";
import {
  DATASET_V2_PERMISSION,
  ENABLE_PARENT_STANDARDS_TAGGING,
} from "Constants/permissionConstants";

const getResolvedMinimalTreeSet = ({
  unitPlanResolvedValue,
  plannerElementType,
}) => {
  switch (plannerElementType) {
    case ELEMENT_TYPE_PYP_LEARNING_STANDARD:
      return _.get(unitPlanResolvedValue, "benchmarks", []);
    case ELEMENT_TYPE_PYP_ATL:
      return _.get(unitPlanResolvedValue, "atls", []);

    default:
      return _.get(unitPlanResolvedValue, "nodes", []);
  }
};

export const getCollectiveData = ({
  plannerElementType,
  plannerElementNodes = [],
  unitPlanResolvedValue,
  plannerElementNodeSetData,
  value,
  mode = "edit",
  academicSetupType,
}) => {
  const unitPlanNodes = getResolvedMinimalTreeSet({
    unitPlanResolvedValue,
    plannerElementType,
  });

  const allNodes = mergeSelectedNodes({
    nodes: plannerElementNodes,
    selectedNodes: unitPlanNodes,
  });

  const filteredValue = value;

  const getSubjectAndAssociatedParentsDependentData = ({
    shouldAlwaysInclude = false,
  } = {}) => {
    const {
      plannerElementSubType,
      parentNodes = [],
    } = plannerElementNodeSetData;

    const unitPlanNodeSet =
      getPYPElementSetFromNodes({ nodes: unitPlanNodes }) || {};

    // Filtered value will contain only those nodes for which subjects are mapped
    const filteredValue = [];
    const extraSelectedValuesSet = new Set(value);
    const subjectIds = new Set(_.map(parentNodes, "id"));

    // Extract and add them explicitly to each node
    _.forEach(allNodes, node => {
      const subjectId = _.get(
        node,
        "dataSetNodeWrapper.properties.subjects[0].id"
      );

      // Check if node is selected and the subject is mapped
      if (
        (subjectId &&
          subjectIds.has(subjectId) &&
          _.includes(value, node.id)) ||
        (shouldAlwaysInclude && _.includes(value, node.id))
      ) {
        extraSelectedValuesSet.delete(node.id);
        filteredValue.push(node.id);
      }
    });

    const allNodeSet = getPYPElementSetFromNodes({ nodes: allNodes }) || {};
    // parentNodes taken from the plannerElementNodeSetData are subjects

    if (!_.isEmpty(plannerElementSubType)) {
      // For view mode, just return the unitPlanNodeSet with current value
      if (mode === "view") {
        return {
          ...unitPlanNodeSet,
          value,
        };
      }

      return {
        ...allNodeSet,
        selectedValues: filteredValue,
      };
    }
    // For view mode, return unitPlanNodeSet with current value and parent nodes
    if (mode === "view") {
      const isCourseBasedGrading = academicSetupType === COURSE_BASED_GRADING;
      return {
        ...unitPlanNodeSet,
        value: isCourseBasedGrading ? value : filteredValue,
        parentNodes,
        groupByType: isCourseBasedGrading ? null : "SUBJECT",
        shouldAlwaysInclude,
      };
    }

    return {
      ...allNodeSet,
      selectedValues: filteredValue,
      parentNodes,
      groupByType: "SUBJECT",
      extraSelectedValuesSet,
    };
  };

  switch (plannerElementType) {
    case ELEMENT_TYPE_MYP_RELATED_CONCEPT: {
      const allNodeSet = getPYPElementSetFromNodes({ nodes: allNodes }) || {};
      const { parentNodes } = plannerElementNodeSetData;
      if (mode === "view") {
        return {
          ...allNodeSet,
          value,
          parentNodes,
          groupByType: "SUBJECT",
        };
      }
      return {
        ...allNodeSet,
        selectedValues: filteredValue,
        parentNodes,
        groupByType: "SUBJECT",
      };
    }

    case ELEMENT_TYPE_MYP_OBJECTIVES: {
      const unitPlanNodeSet =
        getPYPElementSetFromNodes({ nodes: unitPlanNodes }) || {};
      const allNodeSet = getPYPElementSetFromNodes({ nodes: allNodes }) || {};
      const {
        plannerElementSubType,
        shouldGroupObjectivesBySubject,
        associatedSubjectNodes,
      } = plannerElementNodeSetData;

      if (!_.isEmpty(plannerElementSubType)) {
        // For view mode, just return the unitPlanNodeSet with current value
        if (mode === "view") {
          return {
            ...unitPlanNodeSet,
            value,
          };
        }

        return {
          ...allNodeSet,
          selectedValues: filteredValue,
        };
      }

      const parentNodes = _.map(
        _.get(allNodeSet, "rootNodes", []),
        id => _.find(allNodes, { id }) || {}
      );

      if (mode === "view") {
        return {
          ...unitPlanNodeSet,
          value,
          parentNodes,
          groupByType: "PLANNER_ELEMENT",
          ...(shouldGroupObjectivesBySubject
            ? {
                parentNodes: _.get(
                  plannerElementNodeSetData,
                  "parentNodes",
                  []
                ),
                groupByType: "SUBJECT",
              }
            : {}),
        };
      }

      return {
        ...allNodeSet,
        selectedValues: filteredValue,
        parentNodes,
        associatedSubjectNodes,
        groupByType: "PLANNER_ELEMENT",
      };
    }
    // pyp benchmarks (dependent on subjects)
    case ELEMENT_TYPE_PYP_LEARNING_STANDARD: {
      const { subjects = [] } = plannerElementNodeSetData;

      const parentNodes = subjects;

      // For view mode, return unitPlanNodeSet with current value and parent nodes
      if (mode === "view") {
        const unitPlanNodeSet =
          getPYPElementSetFromNodes({
            nodes: unitPlanNodes,
          }) || {};
        // handle the case where subject is un-mapped from unit plan

        const rootNodes = _.filter(unitPlanNodeSet.rootNodes, id => {
          return _.find(subjects, ({ benchmarkRootNode }) => {
            return id == _.get(benchmarkRootNode, "id");
          });
        });

        // Filtered value will contain only those nodes for which subjects are mapped
        const filteredValue = [];
        const subjectIds = new Set(_.map(subjects, "id"));

        // Extract and add them explicitly to each node
        _.forEach(unitPlanNodeSet.nodes, node => {
          const subjectId = _.get(
            node,
            "dataSetNodeWrapper.properties.subject.id"
          );

          // Check if node is selected and the subject is mapped
          if (
            subjectId &&
            subjectIds.has(subjectId) &&
            _.includes(value, node.id)
          ) {
            filteredValue.push(node.id);
          }
        });

        return {
          ...unitPlanNodeSet,
          rootNodes,
          value: filteredValue,
          parentNodes,
        };
      }
      const allNodeSet =
        getPYPElementSetFromNodes({
          nodes: allNodes,
          startDepth: 1,
        }) || {};

      const subjectIds = new Set(_.map(subjects, "id"));
      const extraSelectedValuesSet = new Set(value);
      const filteredValues = _.reduce(
        allNodeSet.nodes,
        (result, node) => {
          const nodeId = node.id;
          if (_.includes(value, nodeId)) {
            // Check if the standard node's subject is mapped or removed
            // If the subject is mapped, we will keep the value
            const resolvedSubjectId = _.get(node, "resolvedSubject.id");
            if (resolvedSubjectId && subjectIds.has(resolvedSubjectId)) {
              extraSelectedValuesSet.delete(nodeId);
              result.push(nodeId);
            }
          }
          return result;
        },
        []
      );
      return {
        ...allNodeSet,
        selectedValues: filteredValues,
        parentNodes,
        groupByType: "SUBJECT",
        allNodes,
        extraSelectedValuesSet,
      };
    }

    // grade dependent planner element types
    case ELEMENT_TYPE_PYP_ATL:
    case ELEMENT_TYPE_MYP_ATL:
    case ELEMENT_TYPE_DP_ATL:
    case ELEMENT_TYPE_UBD_ATL:
    case ELEMENT_TYPE_UBD_SKILL: {
      const unitPlanNodeSet =
        getPYPElementSetFromNodes({ nodes: unitPlanNodes }) || {};

      const allNodeSet = getPYPElementSetFromNodes({ nodes: allNodes }) || {};

      // For view mode, return unitPlanNodeSet with current value
      if (mode === "view") {
        return {
          ...unitPlanNodeSet,
          value,
          parentNodes: _.map(
            _.get(unitPlanNodeSet, "rootNodes", []),
            id => _.find(unitPlanNodes, { id }) || {}
          ),
          groupByType: "PLANNER_ELEMENT",
        };
      }

      return {
        ...allNodeSet,
        selectedValues: filteredValue,
        parentNodes: _.map(
          _.get(allNodeSet, "rootNodes", []),
          id => _.find(allNodes, { id }) || {}
        ),
        groupByType: "PLANNER_ELEMENT",
      };
    }
    // here we don't have parentNodes
    case ELEMENT_TYPE_MYP_SPECIFIED_CONCEPT:
    case ELEMENT_TYPE_DP_KNOWLEDGE_FRAMEWORK: {
      const unitPlanNodeSet =
        getPYPElementSetFromNodes({ nodes: unitPlanNodes }) || {};
      const allNodeSet = getPYPElementSetFromNodes({ nodes: allNodes }) || {};
      if (mode === "view") {
        return {
          ...unitPlanNodeSet,
          value,
          groupByType: "PLANNER_ELEMENT",
        };
      }

      return {
        ...allNodeSet,
        selectedValues: filteredValue,
        groupByType: "PLANNER_ELEMENT",
      };
    }

    // subject dependent planner element types
    case ELEMENT_TYPE_MYP_LEARNING_STANDARD:
    case ELEMENT_TYPE_DP_LEARNING_STANDARD:
    case ELEMENT_TYPE_UBD_LEARNING_STANDARD:
    case ELEMENT_TYPE_DP_SYLLABUS: {
      return getSubjectAndAssociatedParentsDependentData();
    }
    default: {
      return getSubjectAndAssociatedParentsDependentData({
        shouldAlwaysInclude: true,
      });
    }
  }
};

/*
This memoized function is used only in "view" mode, so 'mode' is excluded from the cache key.
Memoization key is based on:
- plannerElementType
- unitPlanResolvedValue: resolved nodes are extracted via getResolvedMinimalTreeSet and memoized by their IDs
- value: array of selected node IDs, used to track selection changes
- plannerElementNodeSetData: contains parent nodes (used because parents like subjects could be unmapped in realTime)
*/
export const getCollectiveDataMemoized = _.memoize(
  getCollectiveData,
  ({
    plannerElementType,
    unitPlanResolvedValue,
    value,
    plannerElementNodeSetData,
  }) => {
    const nodes = getResolvedMinimalTreeSet({
      unitPlanResolvedValue,
      plannerElementType,
    });

    const nodeIds = _.sortBy(_.map(nodes, "id")).join(",");
    const selectedIds = _.sortBy(value).join(",");
    const { parentNodes = [] } = plannerElementNodeSetData;
    const parentNodeIds = _.sortBy(_.map(parentNodes, "id")).join(",");
    // To do: make subjects as parentNodes in plannerElementNodeSetData in pyp
    const subjectIds = _.sortBy(
      _.map(plannerElementNodeSetData.subjects || [], "id")
    ).join(",");
    return `${plannerElementType}|${nodeIds}|${selectedIds}|${parentNodeIds}|${subjectIds}`;
  }
);

const suffixComponent = ({ count }) => {
  return (
    <Badge
      type="numeric"
      showZero="true"
      variant="subtle"
      value={count}
      dsVersion="2.0"
    />
  );
};

export const getSideNavigationOptions = ({ parentNodes, parentNodesCount }) => {
  const rootOption = {
    key: "root",
    children: _.map(parentNodes, ({ id }) => id),
  };

  const options = _.map(parentNodes, ({ id, name, label }) => ({
    key: id,
    title: name || label,
    tier: "1",
    parent: "root",
    children: [],
    suffix: suffixComponent({ count: _.get(parentNodesCount, id, 0) }),
  }));

  return [rootOption, ...options];
};

// Unified filter function that applies all filters using switch case
const applyFilterToNode = (node, filterType, filterData) => {
  switch (filterType) {
    case "GRADES": {
      const { selectedGradesObj } = filterData;
      return _.some(
        node?.dataSetNodeWrapper?.properties?.grades,
        ({ id }) => selectedGradesObj[id]
      );
    }

    case "SUBJECT_GROUPS": {
      const { selectedSubjectGroupsSet } = filterData;
      return _.some(
        _.get(node, "dataSetNodeWrapper.properties.subjectGroups", []),
        subjectGroup => selectedSubjectGroupsSet.has(subjectGroup.id)
      );
    }

    case "STATUS": {
      const { statusFilter } = filterData;
      const isArchived = _.get(
        node,
        "dataSetNodeWrapper.properties.isArchived",
        false
      );

      if (statusFilter === "ACTIVE") return !isArchived;
      if (statusFilter === "ARCHIVED") return isArchived;

      return true;
    }

    case "SEARCH": {
      const { lowerCaseSearchText } = filterData;
      const nodeLabel = _.toLower(node.label || "");
      const nodeCode = _.toLower(node.code || "");

      return (
        nodeLabel.includes(lowerCaseSearchText) ||
        nodeCode.includes(lowerCaseSearchText)
      );
    }

    case "TAGS": {
      const { selectedTagsMap } = filterData;
      const nodeTags = _.get(node, "dataSetNodeWrapper.properties.tags", []);

      return _.some(nodeTags, tag => selectedTagsMap[tag.id]);
    }

    default:
      return true;
  }
};

// Helper function to find all descendants of a node
const findAllDescendants = (nodeId, nodesByIdMap, descendantsSet) => {
  const node = nodesByIdMap[nodeId];
  if (!node) return;

  descendantsSet.add(nodeId);
  // Find all children nodes and recursively get their descendants
  _.forEach(node.children, childNodeId => {
    if (!descendantsSet.has(childNodeId)) {
      findAllDescendants(childNodeId, nodesByIdMap, descendantsSet);
    }
  });
};

// Helper function to find all ancestors of a node
const findAllAncestors = (nodeId, nodesByIdMap, ancestorsSet) => {
  const node = nodesByIdMap[nodeId];
  if (!node) return;

  ancestorsSet.add(nodeId);

  const parentId = node.parent;
  if (parentId && nodesByIdMap[parentId] && !ancestorsSet.has(parentId)) {
    findAllAncestors(parentId, nodesByIdMap, ancestorsSet);
  }
};

// Helper function to apply search filter and get descendants
const applySearchFilterWithDescendants = (nodes, nodesByIdMap, searchText) => {
  const searchMatchedNodeIds = new Set();
  const lowerCaseSearchText = _.toLower(searchText);

  _.forEach(nodes, node => {
    const passesSearchFilter = applyFilterToNode(node, "SEARCH", {
      lowerCaseSearchText,
    });

    if (passesSearchFilter) {
      searchMatchedNodeIds.add(node.id);
    }
  });

  // Find all descendants of search-matched nodes
  const searchAndDescendantsSet = new Set();
  searchMatchedNodeIds.forEach(nodeId => {
    findAllDescendants(nodeId, nodesByIdMap, searchAndDescendantsSet);
  });

  return searchAndDescendantsSet;
};

// Helper function to prepare other filters configuration
const prepareOtherFilters = (
  selectedGrades,
  selectedSubjectGroups,
  statusFilter,
  selectedTagIds
) => {
  const otherFilters = [];

  if (_.size(selectedGrades) > 0) {
    otherFilters.push({
      type: "GRADES",
      data: { selectedGradesObj: _.zipObject(selectedGrades, selectedGrades) },
    });
  }

  if (_.size(selectedSubjectGroups) > 0) {
    otherFilters.push({
      type: "SUBJECT_GROUPS",
      data: { selectedSubjectGroupsSet: new Set(selectedSubjectGroups) },
    });
  }

  if (statusFilter !== "ALL") {
    otherFilters.push({
      type: "STATUS",
      data: { statusFilter },
    });
  }

  if (_.size(selectedTagIds) > 0) {
    otherFilters.push({
      type: "TAGS",
      data: { selectedTagsMap: _.keyBy(selectedTagIds) },
    });
  }

  return otherFilters;
};

// Helper function to apply other filters to eligible nodes
const applyOtherFilters = (eligibleNodeIds, otherFilters, nodesByIdMap) => {
  if (_.size(otherFilters) === 0) {
    return eligibleNodeIds;
  }

  const filteredEligibleIds = new Set();

  eligibleNodeIds.forEach(nodeId => {
    const node = nodesByIdMap[nodeId];
    if (!node) return;

    const passesAllOtherFilters = _.every(otherFilters, filter =>
      applyFilterToNode(node, filter.type, filter.data)
    );

    if (passesAllOtherFilters) {
      filteredEligibleIds.add(nodeId);
    }
  });

  return filteredEligibleIds;
};

// Optimized filtering function with new flow: search -> descendants -> other filters -> ancestors
const getFilteredNodesWithAllFilters = ({ nodes, filters = {} }) => {
  const {
    GRADES: selectedGrades = [],
    TAGS: selectedTagIds = [],
    SUBJECT_GROUPS: selectedSubjectGroups = [],
    STATUS: statusFilter = "ALL",
    SEARCH: searchText = "",
  } = filters || {};

  if (
    _.isEmpty(selectedGrades) &&
    _.isEmpty(selectedTagIds) &&
    _.isEmpty(selectedSubjectGroups) &&
    statusFilter === "ALL" &&
    _.isEmpty(searchText)
  ) {
    return nodes;
  }

  const nodesByIdMap = _.keyBy(nodes, "id");
  let eligibleNodeIds = new Set(_.map(nodes, "id"));

  // Step 1: Apply search filter first if present and get descendants
  if (!_.isEmpty(searchText)) {
    eligibleNodeIds = applySearchFilterWithDescendants(
      nodes,
      nodesByIdMap,
      searchText
    );
  }

  // Step 2: Apply other filters (GRADES, SUBJECT_GROUPS, STATUS, TAGS) to eligible nodes
  const otherFilters = prepareOtherFilters(
    selectedGrades,
    selectedSubjectGroups,
    statusFilter,
    selectedTagIds
  );
  eligibleNodeIds = applyOtherFilters(
    eligibleNodeIds,
    otherFilters,
    nodesByIdMap
  );

  // Step 3: Find all ancestors of the final filtered nodes
  const finalNodeIds = new Set();
  eligibleNodeIds.forEach(nodeId => {
    findAllAncestors(nodeId, nodesByIdMap, finalNodeIds);
  });

  // Return filtered nodes based on final node IDs
  return _.filter(nodes, node => finalNodeIds.has(node.id));
};

const getFilteredRootNodes = ({
  nodes,
  rootNodes,
  sideNavigationTab,
  groupByType,
}) => {
  if (!sideNavigationTab) {
    return rootNodes;
  }

  if (groupByType === "SUBJECT") {
    return _.filter(rootNodes, id => {
      const node = _.find(nodes, { id }) || {};
      const subjects = _.get(
        node,
        "dataSetNodeWrapper.properties.subjects",
        []
      );
      return _.find(subjects, { id: sideNavigationTab });
    });
  } else {
    return _.filter(rootNodes, id => id == sideNavigationTab);
  }
};

const getPlannerElementValue = ({ nodes, isStandardsField }) => {
  if (isStandardsField && ACLStore.can(ENABLE_PARENT_STANDARDS_TAGGING))
    return _.map(nodes, ({ id }) => id);

  const leafNodes = _.filter(nodes, ({ children }) => _.isEmpty(children));
  return _.map(leafNodes, ({ id }) => id);
};

export const getFlattenedNestedNodes = ({
  nodes,
  rootNodes,
  sideNavigationTab,
  groupByType,
  filters,
  isStandardsField,
}) => {
  const filteredNodes = getFilteredNodesWithAllFilters({
    nodes,
    filters,
  });

  const filteredRootNodes = getFilteredRootNodes({
    nodes: filteredNodes,
    rootNodes,
    sideNavigationTab,
    groupByType,
  });

  const nestedNodes = generateNestedSelectedData({
    nodes: filteredNodes,
    rootNodes: filteredRootNodes,
    value: getPlannerElementValue({ nodes: filteredNodes, isStandardsField }),
  });

  return getFlattenedNodes({ nodes: nestedNodes });
};

const flattenChildrenNodes = ({ node, flattenedNodes }) => {
  flattenedNodes.push(node);
  if (node.children) {
    _.forEach(node.children, childNode => {
      if (childNode) {
        flattenChildrenNodes({ node: childNode, flattenedNodes });
      }
    });
  }
};

export const getFlattenedNodes = ({ nodes }) => {
  const flattenedNodes = [];
  _.forEach(nodes, node => {
    if (node) {
      flattenChildrenNodes({ node, flattenedNodes });
    }
  });
  return flattenedNodes;
};

export const getPlannerElementNodesQueryVariables = ({
  plannerElementType,
  plannerElementNodeSetData,
  curriculumProgram,
}) => {
  const { id } = plannerElementNodeSetData;

  let plannerElementNodeFilters = {};

  switch (plannerElementType) {
    case ELEMENT_TYPE_PYP_ATL: {
      const { grades = [] } = plannerElementNodeSetData;
      plannerElementNodeFilters = {
        grades,
      };
      break;
    }
    // MB only case
    case ELEMENT_TYPE_UBD_LEARNING_STANDARD: {
      const {
        id: curriculumId,
        nodeInterfaceType: curriculumNodeInterfaceType,
        type: curriculumType,
      } = curriculumProgram;
      const { filters } = plannerElementNodeSetData;
      return {
        plannerElementTypes: [plannerElementType],
        curriculumId,
        curriculumNodeInterfaceType,
        curriculumType,
        filters,
      };
    }

    default: {
      const { filters } = plannerElementNodeSetData;
      if (filters) {
        plannerElementNodeFilters = { ...filters };
      }
    }
  }
  return {
    plannerElementNodeSetId: id,
    plannerElementNodeFilters,
  };
};

export const getColumnProperties = ({ plannerElementType = null }) => {
  const titleWidth = getTitleColumnWidth(plannerElementType);

  const defaultCellConfig = {
    styles: {
      selectedColor: "white",
    },
  };

  return {
    [DATA_GRID_COLUMN_TYPES.GRADE_LEVELS]: {
      headerConfig: {
        tooltip: null,
      },
      customConfig: {
        property: "dataSetNodeWrapper.properties.grades",
      },
      cellConfig: defaultCellConfig,
    },
    [DATA_GRID_COLUMN_TYPES.TAGS]: {
      headerConfig: {
        tooltip: null,
      },
      customConfig: {
        property: "dataSetNodeWrapper.properties.tags",
      },
      cellConfig: defaultCellConfig,
    },
    [DATA_GRID_COLUMN_TYPES.CODE]: {
      headerConfig: {
        tooltip: null,
      },
      cellConfig: defaultCellConfig,
    },
    [DATA_GRID_COLUMN_TYPES.TITLE]: {
      headerConfig: {
        width: titleWidth,
        tooltip: null,
      },
      cellConfig: defaultCellConfig,
    },
    [DATA_GRID_COLUMN_TYPES.DESCRIPTION]: {
      headerConfig: {
        tooltip: null,
        width: 340,
      },
      cellConfig: defaultCellConfig,
    },
    [DATA_GRID_COLUMN_TYPES.STAAR_QUES]: {
      headerConfig: {
        tooltip: null,
      },
      cellConfig: defaultCellConfig,
    },
    [DATA_GRID_COLUMN_TYPES.SELECTION]: {
      cellConfig: defaultCellConfig,
    },
    [DATA_GRID_COLUMN_TYPES.SUBJECT_GROUP]: {
      cellConfig: defaultCellConfig,
      customConfig: {
        property: "dataSetNodeWrapper.properties.subjectGroups",
        showEmptyCellAsFallback: true,
      },
    },
  };
};

const allowableColumns = ["allowCodes", "allowGrades", "allowTags"];
export const getAllowedColumnsFromConfigs = ({ configs }) => {
  const allowedColumns = [];

  _.forEach(configs, value => {
    const configKey = _.get(value, "config", "");
    const configValue = _.get(value, "value", "");
    if (_.includes(allowableColumns, configKey) && configValue === true) {
      allowedColumns.push(configKey);
    }
  });
  return allowedColumns;
};

export const getColumnIdsToDisplay = ({
  plannerElementType,
  allowedColumns = [],
}) => {
  const columnIdsToDisplay = [
    DATA_GRID_COLUMN_TYPES.SELECTION,
    DATA_GRID_COLUMN_TYPES.TITLE,
  ];

  _.forEach(allowedColumns, column => {
    switch (column) {
      case "allowCodes":
        columnIdsToDisplay.push(DATA_GRID_COLUMN_TYPES.CODE);
        break;
      case "allowGrades":
        columnIdsToDisplay.push(DATA_GRID_COLUMN_TYPES.GRADE_LEVELS);
        break;
      case "allowTags":
        columnIdsToDisplay.push(DATA_GRID_COLUMN_TYPES.TAGS);
        break;
    }
  });

  switch (plannerElementType) {
    case ELEMENT_TYPE_MYP_LEARNING_STANDARD:
    case ELEMENT_TYPE_DP_LEARNING_STANDARD:
    case ELEMENT_TYPE_UBD_LEARNING_STANDARD:
      columnIdsToDisplay.push(DATA_GRID_COLUMN_TYPES.DESCRIPTION);
      columnIdsToDisplay.push(DATA_GRID_COLUMN_TYPES.CODE);
      columnIdsToDisplay.push(DATA_GRID_COLUMN_TYPES.GRADE_LEVELS);
      columnIdsToDisplay.push(DATA_GRID_COLUMN_TYPES.TAGS);
      break;
    case ELEMENT_TYPE_PYP_LEARNING_STANDARD:
      columnIdsToDisplay.push(DATA_GRID_COLUMN_TYPES.DESCRIPTION);
      columnIdsToDisplay.push(DATA_GRID_COLUMN_TYPES.CODE);
      columnIdsToDisplay.push(DATA_GRID_COLUMN_TYPES.GRADE_LEVELS);
      columnIdsToDisplay.push(DATA_GRID_COLUMN_TYPES.TAGS);
      columnIdsToDisplay.push(DATA_GRID_COLUMN_TYPES.STAAR_QUES);
      break;
    case ELEMENT_TYPE_DP_SYLLABUS:
      columnIdsToDisplay.push(DATA_GRID_COLUMN_TYPES.CODE);
      columnIdsToDisplay.push(DATA_GRID_COLUMN_TYPES.TAGS);
      break;
    case ELEMENT_TYPE_MYP_RELATED_CONCEPT:
      columnIdsToDisplay.push(DATA_GRID_COLUMN_TYPES.DESCRIPTION);
      break;
    case ELEMENT_TYPE_MYP_ATL:
    case ELEMENT_TYPE_UBD_ATL:
    case ELEMENT_TYPE_UBD_SKILL:
    case ELEMENT_TYPE_DP_ATL:
      columnIdsToDisplay.push(DATA_GRID_COLUMN_TYPES.CODE);
      break;
    case ELEMENT_TYPE_MYP_SPECIFIED_CONCEPT:
      columnIdsToDisplay.push(DATA_GRID_COLUMN_TYPES.SUBJECT_GROUP);
  }
  return columnIdsToDisplay;
};

export const ARCHIVAL_STATE_OPTIONS = t => [
  {
    id: "ALL",
    label: t("common:all"),
  },
  {
    id: "ACTIVE",
    label: t("common:active"),
  },
  {
    id: "ARCHIVED",
    label: t("common:archived"),
  },
];

// STATUS filter for ATL elements only
const ARCHIVE_FILTER_PLANNER_ELEMENT_TYPES = [
  ELEMENT_TYPE_PYP_ATL,
  ELEMENT_TYPE_MYP_ATL,
  ELEMENT_TYPE_DP_ATL,
  ELEMENT_TYPE_UBD_ATL,
];

export const getFilterOverlayOptions = ({
  grades,
  tags,
  subjectGroups,
  plannerElementType,
  t,
  skipFlags = {},
  selectedGrades = [],
  selectedSubjectGroups = [],
}) => {
  const {
    shouldSkipGrades,
    shouldSkipGenericTags,
    shouldSkipSubjectGroups,
  } = skipFlags;

  const options = [];
  const defaultFilterOptions = {};
  const preSelectedFilterOptions = {};

  if (
    ACLStore.can(DATASET_V2_PERMISSION) &&
    _.includes(ARCHIVE_FILTER_PLANNER_ELEMENT_TYPES, plannerElementType)
  ) {
    options.push({
      id: "STATUS",
      label: t("common:status"),
      optionsType: "radio",
      filterOptions: ARCHIVAL_STATE_OPTIONS(t),
    });
    defaultFilterOptions["STATUS"] = "ALL";
    preSelectedFilterOptions["STATUS"] = "ALL";
  }

  if (!shouldSkipGrades) {
    options.push({
      id: "GRADES",
      label: t("scopeAndSequence:grade_levels"),
      optionsType: "checkbox",
      filterOptions: _.map(grades, ({ id, name }) => ({
        id,
        label: name,
      })),
      includeAllOption: true,
      showSearch: true,
    });
    defaultFilterOptions["GRADES"] = [];
    preSelectedFilterOptions["GRADES"] = selectedGrades;
  }

  if (!shouldSkipGenericTags) {
    options.push({
      id: "TAGS",
      label: t("common:tags"),
      optionsType: "checkbox",
      filterOptions: tags,
      includeAllOption: true,
      showSearch: true,
    });
    defaultFilterOptions["TAGS"] = [];
  }

  if (!shouldSkipSubjectGroups) {
    options.push({
      id: "SUBJECT_GROUPS",
      label: t("common:subject_group_plural"),
      optionsType: "checkbox",
      filterOptions: _.map(subjectGroups, ({ id, name }) => ({
        id,
        label: name,
      })),
      includeAllOption: true,
      showSearch: true,
    });
    defaultFilterOptions["SUBJECT_GROUPS"] = [];
    preSelectedFilterOptions["SUBJECT_GROUPS"] = selectedSubjectGroups;
  }

  return {
    filterOptions: options,
    defaultFilterOptions,
    preSelectedFilterOptions,
  };
};
export const getAllFiltersCount = ({ currentFilters }) => {
  return _.reduce(
    currentFilters,
    (acc, filterValue, filterKey) => {
      if (filterKey === "SEARCH") {
        return acc;
      }

      if (filterKey === "STATUS") {
        // Count STATUS filter as applied only if it's not "ALL"
        return filterValue !== "ALL" ? acc + 1 : acc;
      }

      if (!_.isEmpty(filterValue)) {
        return acc + 1;
      }

      return acc;
    },
    0
  );
};

export const getAppliedFiltersCountForFilter = ({ filterValue, filterKey }) => {
  if (filterKey === "STATUS") {
    // For STATUS filter, count as 1 if not "ALL", otherwise 0
    return filterValue !== "ALL" ? 1 : 0;
  }

  if (_.isEmpty(filterValue)) {
    return 0;
  }

  return _.isArray(filterValue) ? _.size(filterValue) : 1;
};

export const getParentNodeCount = ({
  parentNodeId,
  selectedValues,
  idNodesMap,
  plannerElementType,
}) => {
  return _.reduce(
    selectedValues,
    (count, id) => {
      const node = _.get(idNodesMap, id);
      if (!node) return count;

      switch (plannerElementType) {
        case ELEMENT_TYPE_PYP_LEARNING_STANDARD: {
          // Subject directly present in node
          if (node.subject === parentNodeId) {
            return count + 1;
          }
          return count;
        }

        case ELEMENT_TYPE_MYP_LEARNING_STANDARD:
        case ELEMENT_TYPE_MYP_RELATED_CONCEPT:
        case ELEMENT_TYPE_MYP_SPECIFIED_CONCEPT:
        case ELEMENT_TYPE_DP_LEARNING_STANDARD:
        case ELEMENT_TYPE_DP_SYLLABUS:
        case ELEMENT_TYPE_UBD_LEARNING_STANDARD: {
          if (
            _.some(_.get(node, "dataSetNodeWrapper.properties.subjects", []), {
              id: parentNodeId,
            })
          ) {
            return count + 1;
          }
          return count;
        }

        default: {
          // For other types, check recurse till the parent node is null (i.e. till root node)
          let current = node;
          while (_.get(current, "parent")) {
            const parentNode = _.get(idNodesMap, current.parent);
            if (!parentNode) break;
            current = parentNode;
          }

          return _.get(current, "id") === parentNodeId ? count + 1 : count;
        }
      }
    },
    0
  );
};

export const getParentNodesCount = ({
  parentNodes,
  selectedValues,
  idNodesMap,
  plannerElementType,
}) => {
  if (_.size(parentNodes) <= 1) return {};

  return _.reduce(
    parentNodes,
    (acc, parentNode) => {
      const parentId = _.get(parentNode, "id");
      if (!parentId) return acc;

      acc[parentId] = getParentNodeCount({
        parentNodeId: parentId,
        selectedValues,
        idNodesMap,
        plannerElementType,
      });

      return acc;
    },
    {}
  );
};

export const getIdNodesMapMemoized = _.memoize(
  nodes => _.keyBy(nodes, "id"),
  nodes =>
    _.sortBy(nodes, "id")
      .map(n => n.id)
      .join(",")
);

// Returns the appropriate width for the title column based on planner element type
export const getTitleColumnWidth = plannerElementType => {
  switch (plannerElementType) {
    case ELEMENT_TYPE_MYP_LEARNING_STANDARD:
    case ELEMENT_TYPE_DP_LEARNING_STANDARD:
    case ELEMENT_TYPE_UBD_LEARNING_STANDARD:
    case ELEMENT_TYPE_PYP_LEARNING_STANDARD:
      return 600;
    case ELEMENT_TYPE_MYP_RELATED_CONCEPT:
      return 210;
    default:
      return 480;
  }
};

// Returns the column which will acquire the allocated width for the data grid
// By default returns TITLE, for MYP related concept returns DESCRIPTION
export const getFillColumn = ({ plannerElementType }) => {
  if (plannerElementType === ELEMENT_TYPE_MYP_RELATED_CONCEPT) {
    return DATA_GRID_COLUMN_TYPES.DESCRIPTION;
  }
  if (plannerElementType === ELEMENT_TYPE_MYP_SPECIFIED_CONCEPT) {
    return DATA_GRID_COLUMN_TYPES.SUBJECT_GROUP;
  }
  return DATA_GRID_COLUMN_TYPES.TITLE;
};

export const getEdgesFromNodeMap = ({
  nodeMap,
  selectedStandards,
  isParentTaggingEnabled,
}) => {
  return Array.from(
    nodeMap.values(),
    ({ rowId, children, columns, metadata }) => {
      const isSelected = selectedStandards.has(rowId);
      const { isArchived = false } = metadata || {};

      // if node is parent and not selected, disable the checkbox
      if (
        (isArchived && !isSelected) ||
        (!isParentTaggingEnabled && !_.isEmpty(children) && !isSelected)
      ) {
        columns[0].isDisabled = true;
      }
      columns[0].value = getCheckboxValue({
        value: isSelected,
      });

      return {
        rowId,
        children,
        columns,
      };
    }
  );
};

export const getMetadata = ({ node, nodeMap, parentId }) => {
  const { isArchived = false } = node?.dataSetNodeWrapper?.properties || {};
  const parentUID = nodeMap.get(parentId)?.uid;

  return {
    isArchived,
    parentUID,
  };
};
